/* eslint-disable jsx-a11y/prefer-tag-over-role */
import React, { useEffect, useRef, useState } from 'react';
import { Transition } from '@headlessui/react';
import DefaultLink from '../../common/default/DefaultLink';
import DefaultButton from '../../common/default/DefaultButton';
import { HeaderDropdownProps } from '@/types/props/components/layout';
import { HeaderEnum } from '@/types/translations/HeaderEnum';
import useTypedTranslation from '@/hooks/useTypedTranslation';

export default function HeaderMenuDropdown({ label, items }: HeaderDropdownProps) {
  const { translate } = useTypedTranslation<HeaderEnum>('header');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Improved outside click and focus management
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        event.button !== 2 &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };

  return (
    <nav
      ref={dropdownRef}
      className="tw-relative tw-inline-block"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <DefaultButton
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls="header-dropdown-menu"
        onClick={toggleDropdown}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            toggleDropdown();
          }
        }}
        className="tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-offset-2 tw-focus:ring-main/30 tw-flex tw-cursor-pointer tw-items-center tw-text-base tw-font-semibold tw-leading-6 tw-text-textBlack hover:tw-text-main"
      >
        {translate(label as HeaderEnum)}
      </DefaultButton>

      <Transition
        show={isOpen}
        enter="tw-transition tw-ease-out tw-duration-200"
        enterFrom="tw-opacity-0 tw-scale-95"
        enterTo="tw-opacity-100 tw-scale-100"
        leave="tw-transition tw-ease-in tw-duration-150"
        leaveFrom="tw-opacity-100 tw-scale-100"
        leaveTo="tw-opacity-0 tw-scale-95"
        unmount={false}
      >
        <div
          id="header-dropdown-menu"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="dropdown-trigger"
          className="tw-absolute tw-right-0 tw-z-[9999] tw-mt-2 tw-flex tw-min-w-40 tw-items-stretch tw-justify-center tw-gap-4 tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-gray-300 tw-bg-white tw-px-4 tw-py-4 tw-shadow-xl child:tw-flex-grow"
        >
          {items.map((item, itemIndex) => (
            <section key={`dropdown-section-${itemIndex}`} className="tw-last:mb-0 tw-mb-4">
              <div
                className="tw-mb-2 tw-whitespace-nowrap tw-px-2 tw-text-base tw-font-semibold tw-text-textBlack"
                role="presentation"
              >
                {translate(item.title as HeaderEnum)}
              </div>
              <fieldset
                aria-label={item.title}
                className="tw-list-none tw-space-y-1 tw-border-0 tw-p-0"
              >
                {item.children.map((child, childIndex) => (
                  <div key={`dropdown-item-${itemIndex}-${childIndex}`} role="none">
                    {child.href ? (
                      <DefaultLink
                        href={child.href}
                        role="menuitem"
                        className="tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-main/30 tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-whitespace-nowrap tw-rounded-md tw-px-2 tw-py-1 tw-text-left tw-text-sm tw-font-medium tw-leading-6 tw-text-gray-700 hover:tw-bg-main/10"
                      >
                        {translate(child.label as HeaderEnum)}
                      </DefaultLink>
                    ) : (
                      <DefaultButton
                        role="menuitem"
                        onClick={child.onClick}
                        className="tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-main/30 tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-whitespace-nowrap tw-rounded-md tw-px-2 tw-py-1 tw-text-left tw-text-sm tw-font-medium tw-leading-6 tw-text-gray-700 hover:tw-bg-main/10"
                      >
                        {translate(child.label as HeaderEnum)}
                      </DefaultButton>
                    )}
                  </div>
                ))}
              </fieldset>
            </section>
          ))}
        </div>
      </Transition>
    </nav>
  );
}
